import type { EstimateDetail, MappedTuple, Tuple } from "@/interfaces/estimate-detail";
import type { LineItemData, UpdateLineItemParams } from "@/interfaces/payloads/line-item";

export const getDetailFromPossiblyMergedPage = (
  copyState: EstimateDetail[],
  pageId: string,
  tupleId: string,
): { currentPageIndex: number; tuples: Tuple[]; tupleIndex: number } => {
  let currentPageIndex = copyState.findIndex((detail) => detail.dataForUI.metadata.pageId === pageId);
  let tuples = copyState?.[currentPageIndex]?.dataForUI.page.tuplesOutput || [];
  let tupleIndex = tuples.findIndex((tuple) => tuple.tupleId === tupleId);

  if (tupleIndex === -1) {
    const mergedPage = copyState.find((page) => page.dataForUI.page.mergeWith === pageId);
    if (mergedPage) {
      tuples = mergedPage.dataForUI.page.tuplesOutput;
      tupleIndex = tuples.findIndex((tuple) => tuple.tupleId === tupleId);
      pageId = mergedPage.dataForUI.metadata.pageId;
      currentPageIndex = copyState.findIndex((detail) => detail.dataForUI.metadata.pageId === pageId);
    }
  }

  return { currentPageIndex, tuples, tupleIndex };
};

export const applyUpdateToState = (
  currentState: EstimateDetail[],
  updateParams: UpdateLineItemParams,
): {
  modifiedState: EstimateDetail[] | null;
  apiData: {
    pageIdForApi: string;
    tupleData: LineItemData;
    tupleId: string;
  } | null;
} => {
  const {
    pageId: initialPageId,
    tupleId,
    item,
    quantity,
    measurement,
    formula,
    confirmed,
    newLineItemId,
    orderIndex,
    comment,
    skipOrderIndexUpdate = false,
    movedByUser,
    uniqueId,
  } = updateParams;

  let pageId = initialPageId;

  const { currentPageIndex, tuples, tupleIndex } = getDetailFromPossiblyMergedPage(currentState, pageId, tupleId);
  const room = currentState[currentPageIndex];

  if (!room || !tuples[tupleIndex]) {
    console.error("[applyUpdateToState] Update failed: Page or tuple not found");
    return { modifiedState: null, apiData: null };
  }

  pageId = room.dataForUI.metadata.pageId;

  const sourceTuple = tuples[tupleIndex];
  const lineItemIndex = sourceTuple.lineItems.findIndex((lineItem) => lineItem.uniqueId === uniqueId);

  if (lineItemIndex === -1) {
    console.error("[applyUpdateToState] Update failed: Line item not found");
    return { modifiedState: null, apiData: null };
  }

  const oldLineItem = sourceTuple.lineItems[lineItemIndex];
  const updatedLineItem = { ...oldLineItem };

  if (item !== undefined) updatedLineItem.lineItem = item;
  if (quantity !== undefined && measurement !== undefined) {
    updatedLineItem.qnm = [{ quantity, measurement }];
  }
  if (formula !== undefined) updatedLineItem.formula = formula;
  if (confirmed !== undefined) updatedLineItem.confirmed = confirmed;
  if (newLineItemId !== undefined) updatedLineItem.lineItemId = newLineItemId;
  if (orderIndex !== undefined) updatedLineItem.orderIndex = orderIndex;
  if (comment !== undefined) updatedLineItem.comment = comment ?? "";
  if (movedByUser !== undefined) updatedLineItem.movedByUser = movedByUser ?? "";

  const updatedLineItems = sourceTuple.lineItems.map((li, i) => (i === lineItemIndex ? updatedLineItem : li));

  const isIssue = sourceTuple.hasError;
  const updatedTupleData: Tuple = {
    ...sourceTuple,
    lineItems: updatedLineItems,
    ...(!isIssue &&
      orderIndex !== undefined &&
      lineItemIndex === 0 &&
      !skipOrderIndexUpdate && {
        originalTupleIndex: orderIndex,
      }),
  };

  const newTuples = tuples.map((t, i) => (i === tupleIndex ? updatedTupleData : t));
  const newRoom: EstimateDetail = {
    ...room,
    dataForUI: {
      ...room.dataForUI,
      page: {
        ...room.dataForUI.page,
        tuplesOutput: newTuples,
      },
    },
  };
  const modifiedState = currentState.map((r, i) => (i === currentPageIndex ? newRoom : r));

  return {
    modifiedState,
    apiData: {
      pageIdForApi: pageId,
      tupleData: updatedTupleData as unknown as LineItemData,
      tupleId,
    },
  };
};

/**
 * Threads state across iterations so multiple line items sharing a tupleId
 * accumulate all their modifications into a single consolidated tupleData.
 * Returns null if any update fails (page/tuple/lineItem not found).
 */
export const buildLineItemUpdateBatch = (
  startingState: EstimateDetail[],
  pageId: string,
  lineItems: MappedTuple[],
): { lineItemData: LineItemData[] } | null => {
  let workingState: EstimateDetail[] = startingState;
  const tupleDataByTupleId = new Map<string, LineItemData>();

  for (const { tuple, currentItem } of lineItems) {
    const existingQnm = currentItem.qnm[0];
    const { modifiedState, apiData } = applyUpdateToState(workingState, {
      pageId,
      tupleId: tuple.tupleId,
      uniqueId: currentItem.uniqueId,
      lineItemId: currentItem.lineItemId,
      item: currentItem.lineItem,
      quantity: existingQnm ? existingQnm.quantity || 0 : undefined,
      measurement: existingQnm ? existingQnm.measurement || "" : undefined,
      formula: currentItem.formula,
      confirmed: currentItem.confirmed,
      orderIndex: currentItem.orderIndex,
      movedByUser: currentItem.movedByUser,
    });

    if (apiData === null || modifiedState === null) {
      return null;
    }

    workingState = modifiedState;
    tupleDataByTupleId.set(apiData.tupleId, apiData.tupleData);
  }

  return { lineItemData: Array.from(tupleDataByTupleId.values()) };
};
